/**
 * orderProcessor.plugin.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Follows the same pattern as DateBus / ActionBus / EventBus:
 *   export const OrderProcessorBus = new Vue({ methods: { ... } })
 *
 * Registered in main.js inside the global MBS mixin:
 *   import { OrderProcessorBus } from './plugins/process/orderProcessor.plugin'
 *
 *   Vue.mixin({
 *     data() {
 *       return {
 *         MBS: {
 *           events:         EventBus,
 *           actions:        ActionBus,
 *           eis:            EisBus,
 *           data:           DataBus,
 *           text:           TextBus,
 *           date:           DateBus,
 *           orderProcessor: OrderProcessorBus,   // ← add this line
 *         }
 *       }
 *     }
 *   })
 *
 * Call from any component:
 *   await this.MBS.orderProcessor.CREATE_ORDER(this)
 *   await this.MBS.orderProcessor.SUBMIT_ORDER(this, s_order, account_type, payment_option)
 *   await this.MBS.orderProcessor.RECEIPT_PRINTER(this, payload)
 *   await this.MBS.orderProcessor.GENERATE_ORDER_ID(this, payload)
 *
 * Every method receives the calling component as its first argument `ctx`.
 * This gives the bus access to ctx.$store, ctx.MBS, ctx.CurrentTill, etc.
 * without the bus itself needing to be mounted inside a component.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import Vue from 'vue'
import DATA from '../DATA'
import DB from '../db/localDB'
import { store } from '../../store'
import { generateInvoicePayload,toJulianDate,calculateVAT,decryptInvoiceNumber } from '../mra_eis';
const PAGE_NAME = "OrderProcessor"

// ─── Pure helpers ─────────────────────────────────────────────────────────────

/**
 * Resolve the human-readable document print title.
 */
function resolveDocTitle({ doc_print_title, bill_sale_order, duplicate, order_type, order_account_type }) {
    if (doc_print_title) return doc_print_title
    if (bill_sale_order) return 'BILL ORDER'
    if (duplicate) {
        return order_account_type === 'on_account' ? 'DUPLICATE INVOICE' : 'DUPLICATE RECEIPT'
    }
    if (order_type === 'quotation')          return 'SALES QUOTATION'
    if (order_account_type === 'on_account') return 'SALES INVOICE'
    return 'SALES RECEIPT'
}

/**
 * Derive path_name and use_uniform from order + company settings.
 */
function resolveOrderIdMeta({ order_type, account_type, settings, MBS_actions }) {
    const up = str => MBS_actions.TEXT_UP(str)
    const s  = settings ?? {}

    if (order_type === 'quotation')        return { path_name: up(DATA.ITEMS.QUOTATIONS.values),       use_uniform: s.use_uniform_quotation_number ?? null }
    if (account_type === 'on_account')     return { path_name: up(DATA.ITEMS.INVOICES.values),         use_uniform: s.use_uniform_invoice_number   ?? null }
    if (account_type === 'bill_sale_order')return { path_name: up(DATA.ITEMS.BILL_SALE_ORDERS.values), use_uniform: s.use_uniform_receipt_number   ?? null }
    return                                        { path_name: up(DATA.ITEMS.RECEIPTS.values),         use_uniform: s.use_uniform_receipt_number   ?? null }
}

// ─── Bus ──────────────────────────────────────────────────────────────────────

export const OrderProcessorBus = new Vue({

    methods: {

        // ═════════════════════════════════════════════════════════════════════
        // PUBLIC API
        // ═════════════════════════════════════════════════════════════════════

        /**
         * CREATE_ORDER(ctx, return_order?)
         *
         * Pre-payment stage. Builds order_details from selected items + cashup
         * state, validates stock, checks EIS/connectivity, then either:
         *   • returns the raw object when return_order=true
         *   • sets ctx.pay_order and emits ON_PAYMENT_SUMMARY
         *   • shows an appropriate error dialog
         *
         * @param {Vue}     ctx           The calling component instance.
         * @param {boolean} return_order  When true, skip routing and return the
         *                                raw order_details (quotation/bill flows).
         */
        async CREATE_ORDER(ctx, return_order) {
            try {
                const settings = ctx.CompanySettings
                const till     = ctx.CurrentTill 
                const online                 = ctx.onlineLan

                // Card: set pay mode if a card is selected
                if (ctx.SELECTED_CARD) {
                    ctx.SELECTED_CARD_OPTION = 'pay'
                }

                // Item order: carry over customer
                const item_order = ctx.item_order
                if (item_order?.customer_key) {
                    ctx.input.customer_key = item_order.customer_key
                }
                // if (item_order?.customer_code) {
                //     ctx.input.customer_code = item_order.customer_code
                // }
                if (item_order?.customer) {
                    const customer = item_order?.customer
                    ctx.input.customer_code = customer?.code || null
                    ctx.input.customer_name = customer?.name || null
                    ctx.input.customer_tin = customer?.customer_tin || null
                    ctx.input.buyerTIN = customer?.customer_tin || null
                    ctx.input.buyerName = customer?.name || null
                }

                ctx.ON_CHANGE_PRINTER({ legal: true })

                // Identifiers
                const till_code   = till?.code        ?? null
                const outlet_code = till?.outlet_code ?? null
                const company_key = ctx.selectedCompany?.key ?? null

                // Cashup context
                const localCashup        = ctx.LocalCashup
                const cashup_key         = localCashup?.key                ?? null
                const assistant_uid      = localCashup?.assistant_uid      ?? null
                const cashier_cashup_key = localCashup?.cashier_cashup_key ?? null
                const till_key           = localCashup?.till_key           ?? null
                const cashier_till_key   = localCashup?.cashier_till_key   ?? null
                const day_shift_key      = localCashup?.day_shift_key      ?? null

                const cashier_data = cashier_cashup_key
                    ? { assistant_uid, cashup_key: String(cashier_cashup_key), assistant_cashup_key: cashup_key, till_key: cashier_till_key, assistant_till_key: till_key }
                    : { cashup_key: String(cashup_key), till_key }

                // Order identity
                const input        = ctx.input ?? {}
                const customer_key = input.customer_key ?? null
                const customer_code = input.customer_code ?? null
                const moment       = ctx.MBS.date.moment
                const update_key   = moment ? moment().valueOf() : null
                const key          = item_order?.key ?? update_key

                // Totals & VAT
                const TotalPrice           = ctx.TotalPrice
                const TotalDiscount        = ctx.TotalDiscount
                const TotalDiscountedPrice = ctx.TotalDiscountedPrice
                const VAT                  = ctx.CalculatedVAT

                // User identity
                const uid  = ctx.us?.uid ?? null
                const tuid = ctx.tus     ?? null 
                // console.log('[OrderProcessorBus] CREATE_ORDER: uid', {
                //     uid,tuid,assistant_uid,us:ctx.us 
                // })

                // Items guard
                const SELECTED_ITEMS = ctx.SELECTED_ITEMS
                const items_size     = ctx.MBS.actions.SIZE(SELECTED_ITEMS)
                if (items_size < 1) {
                    console.warn('[OrderProcessorBus] CREATE_ORDER: no items selected.')
                    return
                }

                // Build base order_details
                let order_details = {
                    ...cashier_data,
                    key,
                    company_key,
                    day_shift_key:    String(day_shift_key),
                    outlet_code,
                    till_code,
                    sum_amount:       TotalDiscountedPrice,
                    total_price:      TotalPrice,
                    total_discount:   TotalDiscount,
                    order_type:       DATA.ITEMS.SALE_ORDERS.value,
                    created_uid:      tuid ?? uid,
                    local_created_at: key,
                 
                }

                // Merge fields from existing item_order
                if (item_order) {
                    const overrides = {
                        ...(item_order.key                 && { key:                          item_order.key                }),
                        ...(item_order.order_option_key    && { order_option_key:             item_order.order_option_key   }),
                        ...(item_order.created_uid         && { order_created_uid:            item_order.created_uid        }),
                        ...(item_order.till_code           && { order_original_till_code:     item_order.till_code          }),
                        ...(item_order.cashup_key          && { order_original_cashup_key:    item_order.cashup_key         }),
                        ...(item_order.day_shift_key       && { order_original_day_shift_key: item_order.day_shift_key      }),
                        ...(item_order.sage_invoice_number && { sage_invoice_number:          item_order.sage_invoice_number }),
                        ...(item_order.sage_sync_date      && { sage_sync_date:          item_order.sage_sync_date }),
                        ...(item_order.sage_invoice_date   && { sage_invoice_date:          item_order.sage_invoice_date }),
                    }
                    order_details = { uploaded: false, ...order_details, ...overrides }
                }

                if (customer_key) order_details.customer_key = customer_key
                if (customer_code) order_details.customer_code = customer_code
                if (VAT?.summary) order_details.vat_summary  = VAT.summary

                // Build items array
                order_details.items = SELECTED_ITEMS.map(el => ({
                    sold_price:                 ctx.toNumber(el.sold_price),
                    sold_cost_price:            ctx.toNumber(el.average_cost_price) || ctx.toNumber(el.cost_price),
                    quantity:                   ctx.toNumber(el.quantity),
                    discount:                   ctx.toNumber(el.discount),
                    order_key:                  key,
                    update_key:                 el.update_key ?? update_key,
                    pricing_tier_key:           el.pricing_tier?.key ?? el.pricing_tier_key,
                    item_key:                   el._key ?? el.key,
                    key:                        el.key,
                    code:                       el.code,
                    productCode:                el.productCode,
                    name:                       el.name,
                    productName:                el.productName,
                    productDescription:         el.productDescription,
                    taxRateId:                  el.taxRateId,
                    isProduct:                  el.isProduct,
                    is_mra_verified:            el.is_mra_verified,
                    tax_group_code:             el.tax_group_code,
                    price:                      el.price,
                    selected_item_options:      el.selected_item_options,
                    selected_item_option_names: el.selected_item_option_names,
                    _key:                       el._key ?? el.key,
                }))

                // Stock level check
                if (settings?.activate_stock_level) {
                    const stock_settings = await ctx.MBS.actions.CHECK_ITEMS_STOCK_SETTINGS({
                        items: SELECTED_ITEMS, outlet_code, notify: true,
                    })
                    if (stock_settings?.stop_selling) return
                }


                console.log(order_details,"order_details.......");
                

                // Route
                if (return_order) {
                    return order_details
                } else if (till?.eis_blocked_status?.isBlocked) {
                    ctx.BLOCK_TERMINAL()
                } else if (settings?.eis && !till?.eis_configuration) {
                    ctx.MBS.actions.dialog({
                        show: true, fixed: true,
                        title:  'TERMINAL ERROR',
                        text:   'Error, please make sure EIS Terminal is properly configured.',
                        btnYes: 'OKAY', btnNo: 'CANCEL', action: {},
                    })
                } else if (online || settings?.Sell_without_printer) {
                    ctx.pay_order = order_details
                    ctx.MBS.events.$emit('ON_PAYMENT_SUMMARY')
                } else {
                    ctx.MBS.actions.dialog({
                        show: true, fixed: true,
                        title:  'CONNECTOR IS OFF',
                        text:   'Kipcount Connector is offline. Please start the K-Connector and try again.',
                        btnYes: 'TRY AGAIN', btnNo: 'CANCEL',
                        action: { code: `${PAGE_NAME}=K-CONNECT-CREATE-ORDER`, return_order },
                    })
                }
                return order_details
            } catch (error) {
                ctx.MBS.actions.error({ error, from: 'CREATE_ORDER', page: PAGE_NAME })
            }
        },

        /**
         * SUBMIT_ORDER(ctx, s_order, account_type, payment_option)
         *
         * Post-payment stage. Enriches the order with payment/customer/EIS data,
         * validates the account type, attaches an EIS payload, then routes to
         * _submitQuotation or _processSaleOrder.
         *
         * @param {Vue}    ctx             The calling component instance.
         * @param {object} s_order         Raw order from pay_order.
         * @param {string} account_type    'cash' | 'on_account' | 'payment_option' | 'multiple'
         * @param {object} payment_option  Selected payment option object (or null).
         */
        async SUBMIT_ORDER(ctx, s_order, account_type, payment_option) {
            if (!s_order) {
                console.warn('[OrderProcessorBus] SUBMIT_ORDER: s_order is required.')
                return
            }

            let metadata = {
                created_at:Date.now(), 
            }
            ctx.inputLoading = true
            ctx.MBS.actions.progressDialog({
                show:true,
                fixed:false,
                title:"Submitting...."
            }) 
            try {
                const submit_order = this._buildOrder(ctx, s_order, account_type, payment_option)

                if (ctx.created_document_type === 'quotation') {
                    return await this._submitQuotation(ctx, submit_order)
                }

                this._validateAccountType(account_type, submit_order)
                const fiscal = await this._checkFiscalPrinter(ctx, submit_order) 
                const isFiscalPrinter = fiscal?.isFiscalPrinter  
                let processed_order = fiscal?.order  
                metadata.isFiscalPrinter = isFiscalPrinter
                if (!isFiscalPrinter && ctx.CompanySettings?.eis) {
                    this._attachEisPayload(ctx, submit_order)
                    metadata.submitted_start_at = Date.now();
                    const eis_result = await ctx.MBS.eis.SUBMIT_EIS_RECEIPT(ctx,submit_order)
                    metadata.submitted_complete_at = Date.now();
                    metadata.submit_time = Date.now()-metadata.submitted_start_at;
                    metadata.response_time = eis_result?.response_time || null;
                    metadata.isEIS = !!eis_result?.isEIS  
                    processed_order = eis_result?.order 
                    console.log(eis_result,'eis_result..................yy');
                    if (!eis_result?.continue_offline) throw new Error(eis_result)
                    this._displaySubmittedSaleFeedback(ctx, eis_result) 
                }
                
                metadata.first_saved_at = Date.now();
                await store.dispatch('add_items', { name: "CURRENT_SUBMITTED_ORDER", data: processed_order })
                await this._saveSubmittedSaleOrder(ctx,processed_order,metadata)
                await this._printSubmittedSaleOrder(ctx,processed_order,metadata)  
                await this._finalizeUI(ctx,processed_order)

            } catch (error) {
                metadata.submit_error = error?.error ||null
                await this._handleSubmitError(ctx, error,s_order,metadata)
            } finally {
                ctx.inputLoading = false
                ctx.MBS.actions.progressDialog()
            }
        },

        /**
         * RECEIPT_PRINTER(ctx, payload)
         *
         * Generates a sequential order ID, resolves the printer, then routes to
         * the correct print path (PDF/no-printer, fiscal, or EPSON).
         *
         * @param {Vue}    ctx      The calling component instance.
         * @param {object} payload  { order, submit_order, doc_type, duplicate, ... }
         * @returns {Promise<{ success, order, joined_order }>}
         */
        async RECEIPT_PRINTER(ctx, payload) {
            const baseResponse = { from: 'RECEIPT_PRINTER', payload }

            return new Promise(async (resolve, reject) => {
                try {
                    const till    = ctx.CurrentTill
                    const printer = ctx.SelectedPrinter ?? ctx.AssignedPrinter({ legal: true, drawer: true })

                    console.log(printer,'pr,,,,,,,,,,,,,,,,');
                    
                    const order            = payload?.order
                    const submit_order     = payload?.submit_order
                    const duplicate        = payload?.duplicate ?? false
                    const doc_type         = payload?.doc_type  ?? 'sale_order'
                    const merge_doc_type   = payload?.merge_doc_type
                    const order_type       = payload?.order_type
                    const receipt_title    = payload?.title ?? null

                    const order_account_type = order?.account_type      ?? null
                    const payment_option_key = order?.payment_option_key ?? null
                    const bill_sale_order    = order_type === 'bill_sale_order'

                    const printer_code      = printer?.code              ?? 0
                    const printer_type_code = printer?.printer_type_code ?? 0

                    // Document title
                    const print_title = resolveDocTitle({
                        doc_print_title: payload?.doc_print_title,
                        bill_sale_order, duplicate, order_type, order_account_type,
                    })
                    ctx.auto_print.print_title = print_title

                    // Generate order ID
                    // const gen_res         = await this.GENERATE_ORDER_ID(ctx, { ...payload, till, printer })
                    const generated_order = payload?.order ?? null
                    const joined_order    = ctx.MBS.actions.JOIN_SALE_ORDER(generated_order)

                    console.log(joined_order,'joined_order.....');
                    

                    // Route by printer type
                    if (!printer_code || ctx.toNumber(printer_type_code) === 11) {
                        return this._handlePdfOrNoPrinter({
                            ctx, resolve, submit_order, generated_order, joined_order,
                            printer, printer_type_code, doc_type, merge_doc_type, print_title,
                        })
                    }

                    // if (!submit_order && !duplicate) {
                    //     return resolve({ success: false })
                    // }

                    // Fiscal printer (type 1) — ID already assigned; resolve immediately
                    if (ctx.toNumber(printer_type_code) === 1) {
                        return resolve({ success: true, order: generated_order, joined_order })
                    }

                    console.log(baseResponse,'baseResponse.............');
                    
                    // EPSON path
                    await this._handleEpsonPrint({
                        ctx, resolve, reject, baseResponse,
                        payload, printer, printer_code, printer_type_code,
                        order, generated_order, joined_order,
                        doc_type, merge_doc_type, print_title, receipt_title,
                        bill_sale_order, duplicate, order_account_type, payment_option_key,
                    }) 
                } catch (error) {
                    console.log(error,'eeeeeee,,,,,,,,,,,'); 
                    reject({ ...baseResponse, error })
                }
            })
        },

        /**
         * GENERATE_ORDER_ID(ctx, payload)
         *
         * Assigns a unique sequential order ID via local LAN server, online
         * Firebase, or a timestamp fallback. Also handles fiscal printer receipt
         * counts for FP700 devices.
         *
         * @param {Vue}    ctx      The calling component instance.
         * @param {object} payload  { order, till, printer, submit_order, duplicate }
         * @returns {Promise<{ order, successful? }>}
         */
        GENERATE_ORDER_ID(ctx, payload) {
            const baseResponse = { from: 'GENERATE_ORDER_ID', payload }

            return new Promise(async (resolve, reject) => {
                try {
                    const submit_order      = payload?.submit_order ?? null
                    const duplicate         = payload?.duplicate    ?? false
                    const till              = payload?.till         ?? null
                    const server_code       = till?.server_code     ?? null
                    const printer           = payload?.printer      ?? null
                    const printer_type_code = printer?.printer_type_code ?? null
                    const isFiscalPrinter   = ctx.toNumber(printer_type_code) === 1

                    let   order        = payload?.order ?? null
                    const account_type = order?.account_type ?? null
                    const order_type   = order?.order_type   ?? null
                    const bill_sale_order = order_type === 'bill_sale_order'

                    const company_key  = ctx.SELECTED_COMPANY?.company_key ?? null
                    const path         = `COMPANIES/${company_key}`

                    const { path_name, use_uniform } = resolveOrderIdMeta({
                        order_type, account_type,
                        settings:    ctx.CompanySettings,
                        MBS_actions: ctx.MBS.actions,
                    })

                    // Early exits
                    if (!submit_order)                       return resolve({ order })
                    if (order.order_id && bill_sale_order)   return resolve({ order })

                   
                    // Non-uniform: fall back to timestamp key
                    if (!use_uniform) {
                        return resolve({
                            order: { ...order, order_id: order.key, order_id_type: path_name, order_id_generator: 'timestamp' },
                        })
                    }

                    // Uniform ID: local server or online Firebase
                    let return_order = null

                    if (server_code) {
                        const lan_res = await ctx.$store.dispatch('lan_set', {
                            action: `CREATE_${path_name}`,
                            name:   path_name,
                            data:   { ...order, order_type: path_name, order_id_generator: 'local_server' },
                            lan:    ctx.LAN,
                        })
                        return_order = lan_res?.data ?? null
                    } else {
                        const code_res = await ctx.MBS.actions.CODE_GENERATE(
                            ctx.MBS.actions.TEXT_UP(`${path}/${path_name}`)
                        )
                        return_order = {
                            ...order,
                            order_id:           code_res?.code ?? null,
                            order_id_type:      path_name,
                            order_id_generator: 'online',
                        }
                    }

                    resolve({
                        order: {
                            ...order,
                            order_id:           return_order.order_id,
                            order_id_generator: return_order.order_id_generator,
                            mkey:               return_order.mkey,
                            mkey_receipts:      return_order.mkey_receipts,
                            _id:                return_order._id,
                            lan_created_at:     return_order.lan_created_at,
                        },
                        successful: true,
                    })

                } catch (error) {
                    ctx.MBS.actions.error({ ...baseResponse, error, success: false })
                    reject({ ...baseResponse, error })
                }
            })
        },
        async PROCESS_SAGE_INVOICE(ctx,payload){  
            try {
                const status = payload?.status || "Printed"
                payload.status = status
                if (!payload?.lan) {
                    const server  = ctx?.CurrentServer
                    payload.lan = server?.api_link
                }
                console.log(payload,'ON_PROCESS_SAGE_INVOICE------------------'); 
                ctx.MBS.actions.progressDialog({
                    show:true,
                    fixed:false,
                    title:"Processing..."
                }) 
                const res = await ctx.MBS.networkEvents.UPDATE_SAGE_INVOICE(payload)
                console.log(res,'res..................yy');   
                ctx.MBS.actions.progressDialog() 
                if (payload?.notify) {
                    let mbs_text = ctx.MBS.text.item_action_success("SAGE ORDER","PROCESSING")
                    ctx.MBS.actions.dialog({
                        show:true,
                        fixed:true,
                        title:mbs_text.title,
                        text:`${mbs_text.text}`,
                        btnYes:mbs_text.btnYes, 
                    })  
                } 
                return res
            } catch (error) {
                console.log(error,'err..................yy'); 
                ctx.MBS.actions.progressDialog() 
                if (payload?.notify) {
                    let mbs_text = ctx.MBS.text.item_action_fail("SAGE ORDER","PROCESSING")
                    ctx.MBS.actions.dialog({
                        show:true,
                        fixed:true,
                        title:mbs_text.title,
                        text:`${mbs_text.text}`,
                        btnYes:mbs_text.btnYes, 
                    }) 
                    
                }
                return error
            }  
        },
        

        // ═════════════════════════════════════════════════════════════════════
        // PRIVATE HELPERS  (prefixed _ — not part of the public API)
        // ═════════════════════════════════════════════════════════════════════

        /** Build the enriched order object from raw sale data + component state. */
        _buildOrder(ctx, s_order, account_type, payment_option) {
            const sold_uid     = ctx.tus?.uid ?? ctx.us?.uid
            const input        = ctx.input    ?? {}
            const item_order   = ctx.item_order
            const customers    = ctx.CustomersData ?? []
            const customer_key = input.customer_key ?? null
            const customer     = customers.find(c => c.key === customer_key) ?? null

            const order = {
                sold: true, sold_uid, ...s_order,
                account_type,
                customer_key,
                buyerTIN:               input.customer_tin ?? customer?.customer_tin ?? null,
                buyerName:              customer?.name     ?? null,
                buyerAuthorizationCode: input.buyerAuthorizationCode ?? null,
                ...(input.receivable_key && { receivable_key:    input.receivable_key }),
                ...(input.transaction_id && { transaction_id:    input.transaction_id }),
                ...(input.description    && { description:       input.description    }),
                ...(payment_option       && { payment_option_key: payment_option.key  }),
                ...(item_order?.waiter_key && { waiter_key: item_order.waiter_key }),
                ...(item_order?.take_away  && { take_away:  item_order.take_away  }),
                ...(item_order?.table_key  && { table_key:  item_order.table_key  }),
            }

            const raw_given = ctx.given_amount ?  ctx.given_amount : input.given_amount
            

            if (raw_given != null) order.given_amount = ctx.toNumber(raw_given)

            if (ctx.inputPayments?.length && account_type === 'multiple') {
                order.input_payments = ctx.inputPayments
                    .filter(p => p.key)
                    .map(p => ({
                        key:                 p.key,
                        code:                p.code                ?? '',
                        given_amount:        p.given_amount        ?? 0,
                        payment_description: p.payment_description ?? '',
                    }))
            }

            if (item_order?.order_type === 'quotation') {
                Object.assign(order, {
                    quotation_key:           item_order.key,
                    quotation_day_shift_key: item_order.day_shift_key,
                    quotation_cashup_key:    item_order.cashup_key,
                })
            }

            return order
        },

        /** Throw early when account_type is missing required data. */
        _validateAccountType(account_type, order) {
            if (account_type === 'on_account' && !order.customer_key && !order.receivable_key) {
                throw new Error('on_account requires a customer or receivable key.')
            }
            if (account_type === 'payment_option' && !order.payment_option_key) {
                throw new Error('payment_option requires a payment option to be selected.')
            }
        },

        /** Attach the EIS fiscal payload when the till has an EIS terminal. */
        _attachEisPayload(ctx, order) {
            const eis_day_invoice_number = ctx.EisInvoiceNumber || 0 
            const till = ctx.CurrentTill
            if (!till?.eis_terminal) return
            order.eis_day_invoice_number = eis_day_invoice_number
            const eis_payload = generateInvoicePayload(
                till, order, null, DATA.EIS.production, ctx.input?.input_exports
            )
            
            if (eis_payload) order.eis_payload = eis_payload
        },

        /** Quotation sub-pipeline. */
        async _submitQuotation(ctx, order) {
            delete order.account_type
            delete order.input_payments
            order.order_type = 'quotation'

            const printed     = await this.RECEIPT_PRINTER(ctx, {
                order, doc_type: 'quotation', order_type: 'sale_order', submit_order: true,
            })
            const final_order = printed?.order ?? order
            const joined      = printed?.joined_order ?? null

            await DB.addItems({ name: DATA.ITEMS.LOCAL_QUOTATIONS.values, data: final_order, update_locals: true })
            await this.RECEIPT_PRINTER(ctx, { order: joined ?? final_order, doc_type: 'quotation', order_type: 'sale_order' })
            await ctx.RESET_ALL_DATA(true)
        },

     

        _checkFiscalPrinter(ctx, order) {
            const baseResponse = { from: 'checkFiscalPrinter', order }

            return new Promise(async (resolve, reject) => {
                try { 
                    const till              = ctx.CurrentTill     ?? null
                    const server_code       = till?.server_code     ?? null
                    const printer           =  ctx.SelectedPrinter    ?? null
                    const printer_type_code = printer?.printer_type_code ?? null
                    const isFiscalPrinter   = ctx.toNumber(printer_type_code) === 1
 

                    // Fiscal printer — print and capture receipt counts
                    if (isFiscalPrinter) {
                        const fp_res = await ctx.$store.dispatch('lan_print', {
                            print_data:   { data: order, processed: true, join: true },
                            printer,
                            lan:          ctx.LAN,
                            print_option: { document_type: 'fp700', document_title: 'RECEIPT', open_drawer: 1 },
                        })
                        const fp_data            = fp_res?.data ?? null
                        const allReceiptCount    = fp_data?.allReceiptCount    ?? null
                        const fiscalReceiptCount = fp_data?.fiscalReceiptCount ?? null

                        order.order_key = allReceiptCount ? `${allReceiptCount}/${fiscalReceiptCount}` : null
                        order.fiscal_receipt = { allReceiptCount, fiscalReceiptCount } 
                    }


                    resolve({
                        order,
                        isFiscalPrinter,
                        successful: true,
                    })

                } catch (error) {
                    ctx.MBS.actions.error({ ...baseResponse, error, success: false })
                    reject({ ...baseResponse, error })
                }
            })
        },
        _saveSubmittedSaleOrder(ctx, submitted_order,metadata,update_locals) {
            return new Promise(async (resolve, reject)=>{
                try {
                    if (!submitted_order ||!ctx) throw new Error('Missing required parameters.')
                    metadata.last_saved_at = Date.now();
                    const saved =  await DB.addItems({
                        name: DATA.ITEMS.LOCAL_SALE_ORDERS.values,
                        data: {
                            ...submitted_order,
                            offline_uploaded:!submitted_order.cloud_uploaded,
                            uploaded:false,
                            metadata
                        },
                        update_locals: update_locals,
                    })
                    if (!saved?.success) throw new Error('Failed to save sale order locally.')
                        
                    if (update_locals) {
                        
                        //sage invoice
                        if(submitted_order?.sage_invoice_number){
                            this.PROCESS_SAGE_INVOICE(ctx,{...submitted_order,notify:false,invoiceNo:submitted_order?.sage_invoice_number})
                        }
                        setTimeout(() => {
                            ctx.MBS.events.$emit('SUBMIT_SALES')
                            ctx.$store.dispatch('fi_uploadSales')
                        }, 100) 
                    }
                    resolve({ success: true, order: submitted_order })
                }catch (error) {
                    const _error={code:"save_error",message:error?.message||error,error_data:error,submitted_order}
                    setTimeout(() => {
                        this._saveSubmittedError(ctx,_error,metadata) 
                        reject({ success: false,error:_error }) 
                    }, 10);
                } 
            } ) 

           
        },
        _saveSubmittedError(ctx,error,metadata){ 
            ctx.MBS.actions.dialog({
                show: true, fixed: true,
                title:  'SAVE ERROR',
                text:  `Failed to save sale order locally. Please contact support. Error:( ${error?.message })`,
                btnYes: 'TRY AGAIN', btnNo: 'CANCEL', 
                action: {
                    code: `SAVE-SALE-ORDER-ERROR`,  
                    error,
                    metadata
                },
            })
        },
        _printSubmittedSaleOrder(ctx, submitted_order,metadata) {
            return new Promise(async (resolve, reject)=>{
                try {
                    if (!submitted_order ||!ctx) throw new Error('Missing required parameters.')
                    metadata.print_start_at = Date.now();
                    ctx.MBS.actions.progressDialog({
                        show:true,
                        fixed:true,
                        title:"Checking..."
                    }) 
                    await this._markItemOrdersSold(ctx, submitted_order.sold_uid)
                    metadata.order_marked = true 

                    ctx.MBS.actions.progressDialog({
                        show:true,
                        fixed:true,
                        title:"Sale Printing..."
                    }) 
                    const printed = await this.RECEIPT_PRINTER(ctx, { 
                        order: submitted_order, 
                        submit_order: false 
                    })
                    if (!printed?.success) throw new Error('Receipt printer returned failure.')
                    metadata.print_success = printed?.success
                    metadata.print_complete_at = Date.now();


                    ctx.MBS.actions.progressDialog({
                        show:true,
                        fixed:true,
                        title:"Sale finalizing..."
                    })
                    const finalized = await this._finalizeUI(ctx,submitted_order)
                    metadata.sale_finalized = true 
                    console.log(finalized,'finalized...............xxxxxx.x.x..x.x.x.'); 
                    if (!finalized?.success) throw new Error('Sale not finalized. Something went wrong.')

                    
                    

                    resolve({ success: true, order: printed.order ?? submitted_order,metadata})
                }catch (error) {
                    console.log(error,'PP***********************--------');
                    ctx.MBS.actions.progressDialog()
                    const error_message = metadata.order_marked?error?.message||error:"Local server error"
                    const _error={code:"printer_error", message:error_message,error_data:error,submitted_order}
                    metadata.error = _error
                    setTimeout(() => {
                        this._printSubmittedError(ctx,_error,metadata) 
                        reject({ success: false,error:_error,metadata }) 
                    }, 10);
                }finally{
                    console.log(metadata,"metadata......................xxx.x.x.x.");
                    
                    ctx.MBS.actions.progressDialog()
                    this._saveSubmittedSaleOrder(ctx,submitted_order,metadata,!!metadata.print_success)
                }
            } )
        }, 
        _printSubmittedError(ctx,error,metadata){
            ctx.MBS.actions.dialog({
                show: true, fixed: true,
                title:  'PRINT ERROR',
                text:  `Failed to print sale order. Please contact support. Error:( ${error.message || error})`,
                btnYes: 'TRY AGAIN', btnNo: 'CANCEL', 
                action: {
                    code: `PRINT-SALE-ORDER-ERROR`,  
                    error,
                    metadata
                },
            })
        },
        _displaySubmittedSaleFeedback(ctx, submitted_order) {
            if (!submitted_order?.message ||!ctx) return
            // const mbs_text = ctx.MBS.text.item_action_success("SALE ORDER","SUBMITTED")
            const message = submitted_order?.message  
            ctx.MBS.actions.dialog({
                show:true,
                fixed:true,
                title:"ATTENTION!.",
                text:`${message}`,
                btnYes:"OK", 
            })  
        },        

        /** Mark source item_order(s) as sold/deleted on the LAN. */
        async _markItemOrdersSold(ctx, sold_uid) {
            const item_order = ctx.item_order
            if (!item_order || !!item_order?.sage_invoice_number) return

            const order_version = ctx.toNumber(item_order.order_version) + 1
            const base = key => ({
                key, deleted: true, sold: true, sold_uid,
                deleting_reason: 'sold', uploaded: false, order_version,
            })
            const multiple = item_order.orders

            if (multiple?.length) {
                const delete_orders = multiple.map(o => ({
                    ...base(o.key), day_shift_key: o.day_shift_key,
                    company_key: o.company_key, created_uid: o.created_uid,
                }))
                const { data } = await ctx.LAN_ORDER({ order: delete_orders, update: true, multiple: true })
                await DB.updateItems({ name: DATA.ITEMS.LOCAL_ITEM_ORDERS.values, data, update_locals: true })
                return
            }

            if (item_order.key) {
                const { data } = await ctx.LAN_ORDER({
                    order: { ...base(item_order.key), day_shift_key: item_order.day_shift_key,
                             company_key: item_order.company_key, created_uid: item_order.created_uid },
                    update: true,
                })
                await DB.updateItems({ name: DATA.ITEMS.LOCAL_ITEM_ORDERS.values, data, update_locals: true })
            }
        },

        /** Reset dialogs, print final receipt, emit summary event. */
        _finalizeUI(ctx, submitted_order) {
            return new Promise(async (resolve,reject)=>{
                try {  
                    

                    ctx.dialog_given_amount = false
                    ctx.dialog_paid_amount  = false
                    ctx.MBS.actions.progressDialog()
                    ctx.CASHIER_LOGIN_TILL('SUBMIT_ORDER') 
                    const pricing_tier  = ctx.pricing_tier
                    ctx.RESET_ALL_DATA(true)
                    ctx.pricing_tier    = pricing_tier
                    ctx.MBS.events.$emit('ON_PAYMENT_SUMMARY',{show:false})
                    resolve({success:true,ctx})
                } catch (error) {
                    reject({error})
                }
            })
        },

        /** PDF / no-printer branch of RECEIPT_PRINTER. */
        _handlePdfOrNoPrinter({ ctx, resolve, submit_order, generated_order, joined_order,
                                 printer, printer_type_code, doc_type, merge_doc_type, print_title }) {
            if (submit_order) {
                return resolve({ success: true, order: generated_order, joined_order })
            }
            ctx.auto_print.printing = false
            setTimeout(() => {
                Object.assign(ctx.auto_print, {
                    auto: true, printer, printing: true,
                    print_data: joined_order, print_title,
                    print_type: doc_type, merge_doc_type,
                    display_option: ctx.toNumber(printer_type_code) === 11 ? 'pdf' : '',
                })
            }, 5)
            resolve({ success: true, order: generated_order, joined_order })
        },

        /** EPSON printer branch of RECEIPT_PRINTER. */
        async _handleEpsonPrint({ ctx, resolve, reject, baseResponse, payload,
                                   printer, printer_code, printer_type_code,
                                   order, generated_order, joined_order,
                                   doc_type, merge_doc_type, print_title, receipt_title,
                                   bill_sale_order, duplicate, order_account_type, payment_option_key }) {
            try {

                console.log({ ctx, resolve, reject, baseResponse, payload,
                                   printer, printer_code, printer_type_code,
                                   order, generated_order, joined_order,
                                   doc_type, merge_doc_type, print_title, receipt_title,
                                   bill_sale_order, duplicate, order_account_type, payment_option_key },"_handleEpsonPrint....");
                
                const order_request_action = doc_type === 'docket'
                    ? ctx.MBS.actions.EPSON_DOCKET_REQUEST
                    : ctx.MBS.actions.EPSON_SALE_ORDER_REQUEST

                const req_res  = await order_request_action({
                    ...payload, printer, printer_code,
                    printer_name: printer.name, printer_type_code,
                    paper_size: printer.paper_size, action: 'sales',
                    SaleOrder: joined_order, sale_order: joined_order,
                    duplicate, com_port: printer.com_port,
                    baud_rate: printer.baud_rate, url: printer.url,
                    print_title, merge_doc_type,
                })

                console.log(req_res,'req_res.........................');
                

                const print_request_data     = req_res?.data ?? null
                const print_request_is_array = Array.isArray(print_request_data)

                let doc_print_title = null
                let open_drawer     = null

                if (doc_type === 'docket' || bill_sale_order || duplicate) {
                    doc_print_title = receipt_title ?? ctx.auto_print.print_title
                } else if (order_account_type === 'on_account') {
                    doc_print_title = receipt_title ?? 'INVOICE'
                } else if (order_account_type === 'payment_option') {
                    doc_print_title = receipt_title ?? 'PAYMENT'
                    if (payment_option_key === 'cash') open_drawer = 1
                } else {
                    open_drawer = 1
                }

                const print_opts = { document_type: doc_type, document_title: doc_print_title, open_drawer }
                const dispatch   = req => ctx.$store.dispatch('lan_print', {
                    print_data: { data: req, processed: true, join: false },
                    printer, lan: ctx.LAN, print_option: print_opts,
                })

                console.log(dispatch,"dispatch......start");
                

                let res = []
                if (print_request_is_array) {
                    for (const request of print_request_data) {
                        const res_ = await dispatch(request)
                        res.push(res_)
                    }
                    // res = await Promise.all(print_request_data.map(dispatch))
                } else {
                    res = await dispatch(print_request_data)
                }
                console.log( res ,'dispatch res++++++');        
                resolve({ success: true, order: generated_order, joined_order })

            } catch (error) {
                console.log(error,'eee,,,,,,,,,,,,,,,...print failed');
                
                const message = error?.error?.message
                    ?? 'Kipcount Connector is offline. Please start the connector and try again.'
                // ctx.MBS.actions.dialog({ show: true, fixed: true, title: 'PRINTING ERROR', text: message, btnYes: 'OK' })
                reject({ ...baseResponse, message, error:error?.error })
            }
        },

        /** Centralized error handler for SUBMIT_ORDER failures. */
        async _handleSubmitError(ctx, error, s_order,metadata) {
            console.error('[OrderProcessorBus] SUBMIT_ORDER error:', error)
            ctx.dialog_given_amount = false
            ctx.dialog_paid_amount  = false
            ctx.MBS.actions.progressDialog()

            const message = error.message
            const error_code = error?.error?.code
            const error_data = error?.error?.error_data

            if (error_code === 'invoice_number_exist') {
                try {
                    const eis_day_invoice_number = ctx.EisInvoiceNumber || 0
                    const last_invoice_number = error_data?.last_invoice?.eis_day_invoice_number || 0
                    const new_invoice_number = Math.max(eis_day_invoice_number, last_invoice_number) + 1
                    console.log(new_invoice_number,'new_invoice_number.............');
                    await ctx.UPDATE_EIS_INVOICE_NUMBER(new_invoice_number)
                } catch (e) {
                    console.error('[OrderProcessorBus] Failed to update EIS invoice number:', e)
                }
            }else if (message === 'Invoice Number already exists') {
                try {
                    ctx.MBS.actions.progressDialog({
                        show:true,
                        fixed:false,
                        title:"Online Invoice Sync...."
                    })
                    const online_last_transaction = await ctx.MBS.eis.ON_LAST_SUBMITTED_ONLINE_TRANSACTION({
                        till_key:s_order?.till_key
                    })
                    const online_transaction_count = ctx.toNumber(online_last_transaction?.invoiceDetails?.transactionCount)
                    console.log(online_last_transaction,"online_last_transaction exists.............");
                    console.log(online_transaction_count,"online_transaction_count.............");
                    
                    ctx.MBS.actions.progressDialog({
                        show:true,
                        fixed:false,
                        title:"Offline Invoice Sync...."
                    })
                    const offline_last_transaction = await ctx.MBS.eis.ON_LAST_SUBMITTED_OFFLINE_TRANSACTION({
                        till_key:s_order?.till_key
                    })
                    const offline_transaction_count = ctx.toNumber(offline_last_transaction?.invoiceDetails?.transactionCount)
                    console.log(offline_last_transaction,"offline_last_transaction exists.............");
                    console.log(offline_transaction_count,"offline_transaction_count.............");

                    const transaction_count = online_transaction_count>offline_transaction_count?online_transaction_count:offline_transaction_count
                     
                    console.log(transaction_count,'transaction_count......'); 
                    await ctx.UPDATE_EIS_INVOICE_NUMBER(transaction_count) 
                    // await ctx.UPDATE_EIS_INVOICE_NUMBER(ctx.EisInvoiceNumber + 100)
                } catch (e) {
                    console.error('[OrderProcessorBus] Failed to update EIS invoice number:', e) 
                }
            }else if (error_code =="printer_error") {
                this._printSubmittedError(ctx,error?.error,metadata)
                return
            }else if (error_code =="save_error") {
                this._saveSubmittedError(ctx,error?.error,metadata)
                return
            }


            const order_key = s_order?.key
            const eis_key = s_order?.eis_payload?.offline?.invoiceNumber

            const printer_error = error.error?.message
            const mbs_text      = ctx.MBS.text.item_action_fail('SALE Order', 'Create')
            ctx.MBS.actions.dialog({
                show: true, fixed: true,
                title:  mbs_text.title,
                text:   `
                    ${order_key?"Key = "+order_key+"\n":""}
                    ${eis_key?"No = "+eis_key+"\n":""}
                    ${mbs_text.text} (${printer_error ?? message})
                `,
                btnYes: mbs_text.btnYes,
            })
        },
    },
})
